import {PlayerAPI} from "@/api/player";
import {MatchReportAPI} from "@/api/match_report";
import {computeGoalkeeperMatchMetrics} from "@/utils/goalkeeperMatchMetrics";

export const matchMetricValue = (row, key) => {
	const metric = row?.statistics?.metrics?.[key];
	return metric
		? metric.conflict
			? null
			: metric.value
		: row?.statistics?.values?.[key] ?? null;
};

export function summarizePlayerMatchProfile(profile) {
	const matches = (profile.matches || []).filter((row) => !row.upcoming);
	const played = matches.filter((row) => row.played);
	const minutesKnown = played.filter(
		(row) => matchMetricValue(row, "minutes") != null,
	);
	const discipline = matches.filter(
		(row) =>
			matchMetricValue(row, "minutes") > 0 &&
			matchMetricValue(row, "yellowCards") != null &&
			matchMetricValue(row, "redCard") != null,
	);
	const disciplineMinutes = discipline.reduce(
		(sum, row) => sum + matchMetricValue(row, "minutes"),
		0,
	);
	const value = (key) => profile.totals?.[key]?.value ?? null;
	return {
		source: "canonical",
		profile,
		matchesPlayed: profile.playedMatches,
		matchesAvailable: profile.pastMatches,
		matchesStarted: played.filter((row) => row.status === "Titolare").length,
		totalMinutes: value("minutes"),
		totalGoals: value("goals"),
		totalAssists: value("assists"),
		totalYellowCards: value("yellowCards"),
		totalRedCards: value("redCard"),
		totalGoalsConceded: value("goalsConceded"),
		saves: value("saves"),
		avgRating: value("rating"),
		goalsPer90: profile.totals?.goals?.per90 ?? null,
		assistsPer90: profile.totals?.assists?.per90 ?? null,
		minutesPerMatch: minutesKnown.length
			? minutesKnown.reduce(
					(sum, row) => sum + matchMetricValue(row, "minutes"),
					0,
				) / minutesKnown.length
			: null,
		cardsPer90:
			disciplineMinutes > 0
				? (discipline.reduce(
						(sum, row) =>
							sum +
							matchMetricValue(row, "yellowCards") +
							matchMetricValue(row, "redCard") * 3,
						0,
					) *
						90) /
					disciplineMinutes
				: null,
	};
}

export async function computeOutfieldPlayerStats(
	playerId,
	seasonId,
	filters = {},
) {
	const profile = await PlayerAPI.matchStatistics(playerId, {
		...filters,
		seasonId,
	});
	return summarizePlayerMatchProfile(profile);
}

export async function computeGoalkeeperSeasonStats(playerId, profile) {
	const eligible = new Map(
		(profile.matches || [])
			.filter(
				(row) => !row.upcoming && row.played && row.statistics?.goalkeeper,
			)
			.map((row) => [row.matchId, row]),
	);
	const reports = (
		(await MatchReportAPI.list({playerId, seasonId: profile.seasonId})) || []
	).filter((report) => eligible.has(report.matchId));
	const counts = new Map();
	for (const report of reports)
		counts.set(report.matchId, (counts.get(report.matchId) || 0) + 1);
	let reportCount = 0;
	let incomplete = reports.some((report) => counts.get(report.matchId) > 1);
	const samples = {};
	await Promise.all(
		reports
			.filter((report) => counts.get(report.matchId) === 1)
			.map(async (report) => {
				try {
					const detail = await MatchReportAPI.getById(report.id);
					if (
						detail?.matchId !== report.matchId ||
						detail?.playerId !== playerId
					) {
						incomplete = true;
						return;
					}
					const row = eligible.get(report.matchId);
					const metrics = computeGoalkeeperMatchMetrics({
						...detail,
						minutesPlayed: matchMetricValue(row, "minutes"),
						totalGoalsConceded: matchMetricValue(row, "goalsConceded"),
						generalRating: matchMetricValue(row, "rating"),
					});
					reportCount++;
					const conceded = matchMetricValue(row, "goalsConceded");
					const saves = matchMetricValue(row, "saves");
					const conflict =
						row.statistics.metrics?.goalsConceded?.conflict ||
						row.statistics.metrics?.saves?.conflict;
					const inconsistent =
						metrics.hasPerShotOutcomes &&
						((conceded != null &&
							conceded !== metrics.pointOutcomeCounts.goal) ||
							(saves != null && saves !== metrics.savesOnTargetValue));
					if (conflict || inconsistent) {
						metrics.seasonSamples.shotStoppingRatio = null;
						metrics.seasonSamples.errorImpactRatio = null;
						incomplete = true;
					}
					for (const [key, sample] of Object.entries(metrics.seasonSamples)) {
						if (!sample) continue;
						if (
							sample.successful < 0 ||
							sample.attempted <= 0 ||
							sample.successful > sample.attempted
						) {
							incomplete = true;
							continue;
						}
						if (!samples[key])
							samples[key] = {successful: 0, attempted: 0, knownMatches: 0};
						samples[key].successful += sample.successful;
						samples[key].attempted += sample.attempted;
						samples[key].knownMatches++;
					}
				} catch {
					incomplete = true;
				}
			}),
	);
	const kpis = Object.fromEntries(
		Object.entries(samples).map(([key, sample]) => [
			key,
			sample.attempted > 0 ? sample.successful / sample.attempted : null,
		]),
	);
	return {
		reportCount,
		incomplete,
		samples,
		kpis: Object.values(kpis).some((v) => v != null) ? kpis : null,
	};
}

export async function loadPlayerRealStats(playerData, seasonId, filters = {}) {
	if (!playerData?.id || !seasonId) return null;
	try {
		const outfield = await computeOutfieldPlayerStats(
			playerData.id,
			seasonId,
			filters,
		);
		const isGoalkeeper = outfield.profile.goalkeeper;
		let goalkeeper = null;
		if (isGoalkeeper) {
			try {
				goalkeeper = await computeGoalkeeperSeasonStats(
					playerData.id,
					outfield.profile,
				);
			} catch {
				goalkeeper = {reportCount: 0, incomplete: true, kpis: null};
			}
		}
		return {outfield, goalkeeper, isGoalkeeper};
	} catch {
		return {
			outfield: null,
			goalkeeper: null,
			error: "Statistiche delle partite non disponibili.",
		};
	}
}

const format = (value, decimals = 0) =>
	value == null
		? "—"
		: Number(value).toLocaleString("it-IT", {maximumFractionDigits: decimals});

export function playerSeasonSummaryCards(realStats) {
	const stats = realStats?.outfield;
	if (!stats) return [];
	const profile = stats.profile;
	const matches = (profile.matches || []).filter((row) => !row.upcoming);
	const completeSum = (values) =>
		values.every((v) => v != null)
			? values.reduce((sum, v) => sum + v, 0)
			: null;
	const teamGoals = (conceded) =>
		completeSum(
			matches.map((row) =>
				!["home", "away"].includes(row.matchSide)
					? null
					: (row.matchSide === "home") !== conceded
						? row.homeGoals
						: row.awayGoals,
			),
		);
	const definitions = realStats.isGoalkeeper
		? [
				[
					"minutes",
					"Minuti",
					completeSum(matches.map((row) => row.sportingMinutes)),
				],
				["goalsConceded", "Gol subiti", teamGoals(true)],
				["saves", "Parate"],
			]
		: [
				[
					"minutes",
					"Minuti",
					completeSum(matches.map((row) => row.sportingMinutes)),
				],
				["goals", "Gol", teamGoals(false)],
				["assists", "Assist", teamGoals(false)],
			];
	definitions.push(
		["yellowCards", "Gialli"],
		["redCard", "Espulsioni"],
		["rating", "Voto medio", 10],
	);
	return [
		{
			key: "played",
			label: "Presenze",
			value: `${format(stats.matchesPlayed)}/${format(stats.matchesAvailable)}`,
			coverage: "Presenze nelle gare trascorse della selezione",
		},
		...definitions.map(([key, label, denominator]) => {
			const total = profile.totals?.[key];
			return {
				key,
				label,
				value:
					format(total?.value, key === "rating" ? 1 : 0) +
					(denominator !== undefined ? "/" + format(denominator) : ""),
				coverage: `${total?.knownMatches ?? 0}/${total?.eligibleMatches ?? 0} gare con dato rilevato`,
			};
		}),
	];
}

export const technicalGroups = [
	{
		title: "Attacco",
		fields: [
			["goals", "Gol"],
			["assists", "Assist"],
			["totalShots", "Tiri totali"],
			["shotsOnTarget", "Tiri in porta"],
			["expectedGoals", "xG"],
			["expectedAssists", "xA"],
		],
	},
	{
		title: "Passaggi",
		fields: [
			["totalPasses", "Passaggi totali"],
			["successfulPasses", "Passaggi riusciti"],
			["progressivePasses", "Passaggi progressivi"],
			["keyPasses", "Passaggi chiave"],
			["throughPasses", "Passaggi filtranti"],
			["killerPass", "Killer pass"],
			["shortHorizontalPassRight", "Corti orizzontali dx"],
			["shortHorizontalPassLeft", "Corti orizzontali sx"],
			["shortVerticalPass", "Corti verticali"],
			["longFrontalPass", "Lunghi frontali"],
			["longPassRight", "Lunghi dx"],
			["longPassLeft", "Lunghi sx"],
		],
	},
	{
		title: "Difesa e duelli",
		fields: [
			["duelsTotal", "Duelli totali"],
			["duelsWon", "Duelli vinti"],
			["aerialDuelsTotal", "Duelli aerei totali"],
			["aerialDuelsWon", "Duelli aerei vinti"],
			["tacklesAttempted", "Contrasti tentati"],
			["tacklesWon", "Contrasti vinti"],
			["interceptions", "Intercetti"],
			["recoveries", "Recuperi"],
			["defensiveCoverages", "Coperture difensive"],
			["defensiveErrors", "Errori difensivi"],
		],
	},
	{
		title: "Possesso e iniziativa",
		fields: [
			["totalTouches", "Tocchi"],
			["touchesInOppositionBox", "Tocchi in area avversaria"],
			["possessionsUnderPressure", "Possessi sotto pressione"],
			["ballsLost", "Palloni persi"],
			["pressures", "Pressioni"],
			["successfulPressures", "Pressioni riuscite"],
			["dribblesAttempted", "Dribbling tentati"],
			["dribblesSuccessful", "Dribbling riusciti"],
			["crossesAttempted", "Cross tentati"],
			["crossesCompleted", "Cross riusciti"],
		],
	},
	{
		title: "Disciplina",
		fields: [
			["yellowCards", "Gialli"],
			["redCard", "Espulsioni"],
			["foulsCommitted", "Falli commessi"],
			["foulsSuffered", "Falli subiti"],
		],
	},
	{
		title: "Portiere",
		goalkeeper: true,
		fields: [
			["goalsConceded", "Gol subiti"],
			["saves", "Parate"],
			["outOfAreaReadings", "Letture fuori area"],
			["lowReadings", "Letture basse"],
			["highReadings", "Letture alte"],
		],
	},
];
export const ratioLabels = {
	shotsOnTarget: "Tiri in porta / tiri totali",
	successfulPasses: "Passaggi riusciti / totali",
	duelsWon: "Duelli vinti / totali",
	aerialDuelsWon: "Duelli aerei vinti / totali",
	tacklesWon: "Contrasti vinti / tentati",
	dribblesSuccessful: "Dribbling riusciti / tentati",
	crossesCompleted: "Cross riusciti / tentati",
	successfulPressures: "Pressioni riuscite / totali",
};
